import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { fetchToursForBlueprint, postCompletingTourBlueprint } from './tour.thunk';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

export const TOUR_NAMES = {
  RESULTS_PAGE: 'blueprint-result-page-tour',
  START_PAGE: 'blueprint-start-page-tour',
} as const;

export type Tour = Readonly<{
  id: number;
  tour_name: string;
  application: 'BLUEPRINT' | 'PROSPER' | 'LUBAV' | 'APP';
  has_user_completed: boolean;
  is_active: boolean;
}>;

type TourState = Readonly<{
  tours: Tour[];
  loading: boolean;
  error?: string;
}>;

const initialState: TourState = {
  tours: [],
  loading: false,
};

export const tourSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    markTourAsComplete: (state, action: PayloadAction<number>) => {
      const updatedTours = state.tours.map((tour) =>
        tour.id === action.payload ? { ...tour, has_user_completed: true } : tour,
      );

      return {
        ...state,
        tours: updatedTours,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchToursForBlueprint.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchToursForBlueprint.fulfilled, (state, action: PayloadAction<Tour[] | null>) => {
        if (!action.payload) {
          state.loading = false;

          return;
        }

        state.tours = action.payload;
        state.loading = false;
      })
      .addCase(fetchToursForBlueprint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(postCompletingTourBlueprint.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        postCompletingTourBlueprint.fulfilled,
        (state, action: PayloadAction<Tour[] | null>) => {
          state.loading = false;
          state.tours = action.payload || [];
        },
      )
      .addCase(postCompletingTourBlueprint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // LOGOUT
      .addCase(logout, (state) => {
        // when we sign out, we just want to reset the has seen tour status for the user
        const toursWithReset = state.tours.map((tour) => ({
          ...tour,
          has_user_completed: false,
        }));

        state.tours = toursWithReset;
        state.loading = false;
        state.error = undefined;
      });
  },
});

// Reducer
export const tourReducer = tourSlice.reducer;

export const selectResultsPageTour = createSelector(
  (state: RootState) => state.tour.tours,
  (tours) =>
    tours?.length ? tours.find((tour) => tour.tour_name === TOUR_NAMES.RESULTS_PAGE) : undefined,
);

export const selectStartPageTour = createSelector(
  (state: RootState) => state.tour.tours,
  (tours) =>
    tours?.length ? tours.find((tour) => tour.tour_name === TOUR_NAMES.START_PAGE) : undefined,
);

// Actions
export const { markTourAsComplete } = tourSlice.actions;
